/**
 * SubscriptionGuard Component
 * 
 * Route protection component that enforces active subscription requirements.
 * Validates subscription status and redirects users without active subscriptions.
 * Integrates with SubscriptionContext for real-time subscription state.
 * 
 * Features:
 * - Subscription status validation
 * - Loading states during subscription checks
 * - Error handling with retry mechanisms
 * - Graceful fallback for subscription check errors
 * - Integration with authentication context
 * - Configurable redirect paths
 */

import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../../auth/hooks';
import { useTranslation } from '../../i18n/hooks';
import { SUBSCRIPTION_STATUS } from '../constants/subscriptionTypes';
import './SubscriptionGuard.css';

/**
 * SubscriptionGuard - Protects routes requiring active subscription
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when subscription is active
 * @param {string} props.redirectTo - Path to redirect users without active subscription (default: '/subscription')
 * @param {string} props.loginRedirectTo - Path to redirect unauthenticated users (default: '/login')
 * @param {boolean} props.showError - Whether to show error messages (default: true)
 * @param {Function} props.onSubscriptionCheck - Callback when subscription check completes
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Subscription guard component
 */
const SubscriptionGuard = ({
  children,
  redirectTo = '/subscription',
  loginRedirectTo = '/login',
  showError = true,
  onSubscriptionCheck,
  className = ''
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const {
    status,
    computed,
    loading,
    error,
    actions
  } = useSubscription();

  const [retryCount, setRetryCount] = useState(0);
  const [checkError, setCheckError] = useState(null);
  const [isInitialCheck, setIsInitialCheck] = useState(true);

  /**
   * Check subscription status on mount and when authentication changes
   */
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || authLoading) {
        return;
      }

      // Skip if subscription is already active or currently loading
      if (computed.isActive || loading.status) {
        setIsInitialCheck(false);
        return;
      }

      try {
        setCheckError(null);
        const result = await actions.checkStatus();
        
        if (result?.error) {
          setCheckError(result.error);
        }

        if (onSubscriptionCheck) {
          onSubscriptionCheck({ success: !result?.error, data: result?.data });
        }
      } catch (err) {
        console.error('🔍 SubscriptionGuard: Subscription check error:', err);
        setCheckError(err.message || t('subscription.errors.checkStatusFailed'));
      } finally {
        setIsInitialCheck(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, authLoading, actions, computed.isActive, loading.status, onSubscriptionCheck, t]);

  /**
   * Handle retry for subscription check errors
   */
  const handleRetry = useCallback(async () => {
    setRetryCount(prev => prev + 1);
    setCheckError(null);
    
    try {
      await actions.checkStatus();
    } catch (err) {
      console.error('🔍 SubscriptionGuard: Retry error:', err);
      setCheckError(err.message || t('subscription.errors.checkStatusFailed'));
    }
  }, [actions, t]);

  /**
   * Render loading state
   */
  const renderLoading = () => (
    <div className={`subscription-guard-loading ${className}`}>
      <div className="subscription-guard-loading__content">
        <div className="subscription-guard-loading__spinner"></div>
        <p className="subscription-guard-loading__message">
          {t('subscription.checking')}
        </p>
      </div>
    </div>
  );

  /**
   * Render error state
   */
  const renderError = () => {
    if (!showError) {
      // Redirect to subscription page if error and showError is false
      return (
        <Navigate
          to={redirectTo}
          state={{ 
            from: location.pathname,
            reason: 'subscription_check_error',
            error: checkError || error
          }}
          replace
        />
      );
    }

    return (
      <div className={`subscription-guard-error ${className}`}>
        <div className="subscription-guard-error__content">
          <div className="subscription-guard-error__icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h2 className="subscription-guard-error__title">
            {t('subscription.errors.checkTitle')}
          </h2>
          <p className="subscription-guard-error__message">
            {checkError || error || t('subscription.errors.checkStatusFailed')}
          </p>
          <div className="subscription-guard-error__actions">
            <button
              type="button"
              className="subscription-guard-error__retry"
              onClick={handleRetry}
            >
              {t('subscription.actions.retry')}
            </button>
            <button
              type="button"
              className="subscription-guard-error__cancel"
              onClick={() => window.history.back()}
            >
              {t('subscription.actions.goBack')}
            </button>
          </div>
          {retryCount > 0 && (
            <p className="subscription-guard-error__retry-count">
              {t('subscription.retryAttempt', { count: retryCount })}
            </p>
          )}
        </div>
      </div>
    );
  };

  // Show loading while authenticating
  if (authLoading) {
    return renderLoading();
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginRedirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Show loading during initial subscription check
  if (isInitialCheck || (loading.status && status === SUBSCRIPTION_STATUS.LOADING)) {
    return renderLoading();
  }

  // Show error state if subscription check failed
  if (checkError || (error && status === SUBSCRIPTION_STATUS.ERROR)) {
    return renderError();
  }

  // Check if subscription is active
  const isSubscriptionActive = computed.isActive && status === SUBSCRIPTION_STATUS.ACTIVE;

  // Redirect if subscription is not active
  if (!isSubscriptionActive) {
    return (
      <Navigate
        to={redirectTo}
        state={{
          from: location.pathname,
          reason: 'subscription_required',
          subscriptionStatus: status,
          expiresAt: computed.expiresAt
        }}
        replace
      />
    );
  }

  // Render protected content for users with active subscription
  return (
    <div className={`subscription-guard ${className}`}>
      {children}
    </div>
  );
};

SubscriptionGuard.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
  loginRedirectTo: PropTypes.string,
  showError: PropTypes.bool,
  onSubscriptionCheck: PropTypes.func,
  className: PropTypes.string
};

export default SubscriptionGuard;

